import type { DropZoneAcceptedFile, DropZoneRejectedItem } from '../types/interfaces'

import { formatFileSize } from './format-file-size'

interface ClassifyFilesOptions {
  accept: string[]
  maxSize: number
  remainingSlots: number
  existingKeys: Set<string>
}

interface ClassifyFilesResult {
  accepted: DropZoneAcceptedFile[]
  rejected: DropZoneRejectedItem[]
}

export const buildFileId = (file: File): string =>
  `${file.name}:${file.size}:${file.lastModified}`

const getExtension = (name: string): string =>
  `.${name.split('.').pop()?.toLowerCase() ?? ''}`

export const classifyFiles = (
  files: File[],
  options: ClassifyFilesOptions,
): ClassifyFilesResult => {
  const { accept, maxSize, remainingSlots, existingKeys } = options
  const accepted: DropZoneAcceptedFile[] = []
  const rejected: DropZoneRejectedItem[] = []
  const seen = new Set(existingKeys)

  const typeReason = `File type must be ${accept.join(', ')}`
  const sizeReason = `File must be smaller than ${formatFileSize(maxSize)}`

  for (const file of files) {
    const id = buildFileId(file)
    if (seen.has(id)) continue
    seen.add(id)

    const base = { id, name: file.name, size: file.size }

    if (!accept.includes(getExtension(file.name))) {
      rejected.push({ ...base, reason: typeReason })
      continue
    }
    if (file.size > maxSize) {
      rejected.push({ ...base, reason: sizeReason })
      continue
    }
    if (accepted.length >= remainingSlots) {
      rejected.push({ ...base, reason: 'Too many files' })
      continue
    }
    accepted.push({ id, file })
  }

  return { accepted, rejected }
}
